/**
 * Built-in Deeply Smart V2 project rules distilled from AGENTS.md.
 * Shown read-only on /coding-standards → Rules, and merged into chat via
 * resolveRules when an attached repo matches distinctive DS layout globs.
 */
import type { DsCoderRule } from "@/types/dsCoderRules";

/** Paths that uniquely identify a Deeply Smart–shaped monorepo. */
export const BUILTIN_DS_CODER_GLOBS =
  "AGENTS.md, supabase_migrations/**, api/embeddings/**, src/plugins/**, vercel.json";

const NOW = "1970-01-01T00:00:00.000Z";

function builtinRule(
  idSuffix: string,
  name: string,
  description: string,
  content: string
): DsCoderRule {
  return {
    id: `builtin:ds-v2-${idSuffix}`,
    scope: "global",
    userId: null,
    workspaceId: null,
    name,
    description,
    content,
    globs: BUILTIN_DS_CODER_GLOBS,
    alwaysApply: false,
    isEnabled: true,
    createdAt: NOW,
    updatedAt: NOW,
  };
}

/**
 * Stable, code-defined rules for Deeply Smart V2. Not stored in Supabase;
 * not editable from the Coding Standards UI.
 */
export const BUILTIN_DS_CODER_RULES: DsCoderRule[] = [
  builtinRule(
    "ai-pipes",
    "AI pipes (OpenRouter + Featherless)",
    "Chat via OpenRouter only; embeddings via Featherless only.",
    `## Do
- Route all main-app chat through OpenRouter via the Supabase Edge \`ai-proxy\`.
- Route all embeddings through Featherless (\`Qwen/Qwen3-Embedding-8B\`, **4000** dims → \`halfvec(4000)\`) via \`/api/embeddings\`.
- Use OpenRouter model slugs for agent \`preferred_model\` and UI model pickers.

## Do not
- Call OpenAI or Anthropic APIs directly for main-app chat.
- Use OpenRouter for embeddings (or treat \`ai-proxy\` \`/embeddings\` as available — it returns 410).
- Put provider API keys in the browser bundle.
- Treat stale docs that say “OpenRouter embeddings / 1536 dims” as current.`
  ),
  builtinRule(
    "vercel-hobby",
    "Vercel Hobby limits",
    "Stay within the 12-function cap and once-per-day cron limit.",
    `## Do
- Add HTTP behavior with \`?route=\` / \`?action=\` inside an **existing** multiplex entrypoint plus a \`vercel.json\` rewrite.
- Prefer Supabase RPC, Edge Functions, or triggers for server-side DB work.
- Chain extra daily work into \`api/clickup/sync?cron=1\` (\`0 6 * * *\`).

## Do not
- Add a new top-level \`api/**/*.ts\` serverless function when multiplexing works (hard cap: **12**).
- Add a second Vercel cron for thread purge or similar (Hobby: **once per day** max).
- Add \`api/threads/audit.ts\` as a separate file — use \`/api/threads/audit\` → \`api/workflows/automation?action=thread-audit\`.`
  ),
  builtinRule(
    "architecture",
    "Architecture layering",
    "types → service → hook → page; match existing shells; dual api/ + server/.",
    `## Do
- Layer features as: types (\`src/types\` or \`shared\`) → \`*Service.ts\` → React Query hook → page/component.
- Use TanStack Query for server/async data; Legend State only for hot UI that must avoid re-renders.
- Match existing shells (Communications inbox layout, plugin host patterns, shadcn UI).
- Implement proxied integrations in **both** \`api/\` and \`server/\` (shared \`_handlers\` preferred).
- Authenticate API calls with \`authHeaders()\` / \`resolveAccessToken()\` when the route requires JWT.

## Do not
- Invent parallel architectures, alternate API names, or second UI shells when a canonical one exists.
- Stuff server cache into Legend as the source of truth.
- Skip the local Express mirror when adding a production Vercel proxy route.`
  ),
  builtinRule(
    "git-secrets",
    "Git workflow and secrets",
    "Branch from main; never commit secrets or push straight to main.",
    `## Do
- Start from latest \`main\`, create a feature/fix branch, push that branch for testing.
- Commit only when the user explicitly asks.
- Keep secrets server-side (\`.env\` never committed).

## Do not
- Commit directly to \`main\` / \`master\`, force-push those branches, or amend already-pushed commits unless explicitly safe and requested.
- Commit \`.env\`, API keys, or credentials.
- Put secrets in client bundles or plugin \`client.js\` (plugins use \`{{secret:KEY}}\` + gateway).`
  ),
  builtinRule(
    "comms-plugins",
    "Communications and plugins",
    "Pattern A inboxes; plugins stay in separate repos with manifest + gateway secrets.",
    `## Communications
- New inbox platforms: Pattern A OAuth (\`startCommunicationOAuth\`), \`CommunicationInboxLayout\`, service + hooks + types, dual \`api/\` + \`server/\`.
- Extend the Communications API catalog — do not invent alternate method names.
- Never use Pattern C (shared env API keys) for new Communications inbox platforms.

## Plugins
- Separate GitHub repo with topic \`deeplysmart-plugin\`; \`plugin.json\` at zip root; \`hostApiVersion\` = current \`HOST_API_VERSION\`.
- Declare permissions upfront; no secrets in the bundle; no bundled React (shim to \`window.DeeplySmart\`).
- Do not add host routes/sidebar entries in \`App.tsx\` / \`AppSidebar.tsx\` for standard plugins — manifest + \`registerPlugin\` own routing.
- Call third-party APIs via \`sdk.fetch\` + \`{{secret:KEY}}\`, never with bearer tokens from the browser.`
  ),
];
